import { hide, jsx, type JsxChild, type JsxElementProps, show } from '@amazon/vinyl-tsx'
import { type ObservableValue } from '@amazon/vinyl-observable'
import { Icon } from '@/components/Icon'
import { modalCount } from '@/style/modalVisible'
import { ctx } from '@/util/context'
import { createDisposer, logDebug } from '@amazon/vinyl-util'
import { onNavigate } from '@/router/router'
import { documentEvents, tabbableQuery, windowEvents } from '@/util/interaction'

export type CloseReason = 'backdrop' | 'keypress' | 'button' | 'navigate'

export interface ModalProps extends JsxElementProps<'div'> {
    readonly visible: ObservableValue<boolean>

    /**
     * Invoked when the user requests the modal to be closed, either via Escape, clicking the background, or
     * clicking the close button.
     */
    readonly onClose: (reason: CloseReason) => void
}

export function Modal({ onClose, className, visible, ...rest }: ModalProps, children: JsxChild[]) {
    const { target } = ctx('Modal')

    return (
        <div
            className={`modal ${className ?? ''}`}
            visible={false}
            {...rest}
            onConnect={(view) => {
                const { add, dispose } = createDisposer()

                let showing = false
                add(
                    visible.onData((value) => {
                        if (showing === value) return // no-op
                        logDebug(target, 'modal visibility:', value)
                        showing = value
                        // Setting the modal count will apply the modalVisible class to the root scrolling element
                        // when a modal is showing.
                        if (value) modalCount.value++
                        else modalCount.value--
                        if (value) {
                            show(view)
                            view.focus()
                        } else hide(view)
                    })
                )

                // Trap focus
                add(
                    documentEvents.on(
                        'focus',
                        (event) => {
                            if (!visible.value) return
                            if (!view.contains(event.target as Node)) {
                                event.stopPropagation()
                                event.preventDefault()

                                const first: HTMLElement | null = view.querySelector(tabbableQuery)
                                first?.focus()
                            }
                        },
                        { capture: true }
                    )
                )

                add(
                    windowEvents.on('keydown', (event) => {
                        if (event.key === 'Escape' && !event.defaultPrevented) {
                            onClose('keypress')
                        }
                    })
                )

                add(() => {
                    if (showing) {
                        modalCount.value--
                    }
                })

                add(
                    onNavigate(() => {
                        onClose('navigate')
                    })
                )

                return dispose
            }}
        >
            <div className="backdrop" onclick={() => onClose('backdrop')} />
            <div className="content">
                <button className="button closeBtn" onclick={() => onClose('button')}>
                    <Icon icon="cancel" />
                </button>
                {...children}
            </div>
        </div>
    )
}
